// src/views/settings/AiSubscriptionPlanForm.jsx
//
// Minimal config form for the AI cost re-basis subscription-fee plan (see
// backend_go cmd/api/ai_cost_handlers.go): monthly fee, plan name, seats, and
// active-from date, submitted to PUT /api/v1/ai-cost/plan.
//
// Standalone component — not yet wired to a route/sidebar entry. Slotting it
// into a settings page and adding nav is a small follow-up.
import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchAiSubscriptionPlan, putAiSubscriptionPlan } from "../litellm/api";

const inputCls =
  "border border-border rounded-ctrl px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary";

// pick reads a plan field defensively. GET /ai-cost/plan serializes the Go `aicost.Plan` struct,
// which has no json tags, so the wire shape is PascalCase (Provider, PlanName, ...) — not the
// snake_case documented for the PUT body (see planRequest in ai_cost_handlers.go). Reading both
// shapes means prefill survives either (reference_domain-structs-pascalcase-json).
function pick(plan, snakeKey, pascalKey) {
  if (!plan) return undefined;
  return plan[snakeKey] !== undefined ? plan[snakeKey] : plan[pascalKey];
}

const BLANK = { provider: "", planName: "", monthlyFeeEuros: "", seats: "", activeFrom: "" };

function planToForm(plan) {
  if (!plan) return BLANK;
  const cents = pick(plan, "monthly_fee_cents", "MonthlyFeeCents");
  const seats = pick(plan, "seats", "Seats");
  const activeFrom = pick(plan, "active_from", "ActiveFrom");
  return {
    provider: pick(plan, "provider", "Provider") || "",
    planName: pick(plan, "plan_name", "PlanName") || "",
    monthlyFeeEuros: cents != null ? String(cents / 100) : "",
    seats: seats != null ? String(seats) : "",
    activeFrom: activeFrom ? String(activeFrom).slice(0, 10) : "",
  };
}

export default function AiSubscriptionPlanForm() {
  const { data: plan, isLoading } = useQuery(["ai-subscription-plan"], () => fetchAiSubscriptionPlan());
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  // Sync from the fetched plan once the initial load resolves — but never once the user has
  // started editing. The GET can resolve well after mount (real network latency); syncing
  // unconditionally on every `plan` change would silently wipe out whatever the user had
  // already typed the moment the response lands.
  const initializedRef = useRef(false);
  const dirtyRef = useRef(false);
  useEffect(() => {
    if (isLoading || initializedRef.current || dirtyRef.current) return;
    initializedRef.current = true;
    setForm(planToForm(plan));
  }, [isLoading, plan]);

  const setField = (key) => (e) => {
    dirtyRef.current = true;
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    const euros = parseFloat(form.monthlyFeeEuros);
    if (Number.isNaN(euros)) {
      setError("Enter a monthly fee.");
      return;
    }
    if (!form.activeFrom) {
      setError("Enter an active-from date.");
      return;
    }
    setBusy(true);
    try {
      await putAiSubscriptionPlan({
        provider: form.provider,
        plan_name: form.planName,
        monthly_fee_cents: Math.round(euros * 100),
        seats: parseInt(form.seats, 10) || 0,
        active_from: form.activeFrom,
      });
      setSaved(true);
    } catch {
      setError("Could not save the plan. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-card-bd rounded-card px-4 py-4 mb-3 max-w-2xl">
      <div className="text-sm font-medium text-text">AI subscription plan</div>
      <p className="text-xs text-text-2 mt-1 mb-3">
        Configure the flat-fee AI subscription (e.g. a seat-based ChatGPT/Copilot plan) used to
        re-base actual AI cost against the API-equivalent (shadow) cost. Saving creates a new
        version rather than overwriting history.
      </p>
      {isLoading && <div className="text-text-2 text-sm mb-3">Loading…</div>}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <label className="text-sm block">
          <span className="block text-text-2 text-xs mb-1">Provider</span>
          <input type="text" value={form.provider} onChange={setField("provider")} className={inputCls} style={{ width: "100%" }} />
        </label>
        <label className="text-sm block">
          <span className="block text-text-2 text-xs mb-1">Plan name</span>
          <input type="text" value={form.planName} onChange={setField("planName")} className={inputCls} style={{ width: "100%" }} />
        </label>
        <label className="text-sm block">
          <span className="block text-text-2 text-xs mb-1">Monthly fee (€)</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.monthlyFeeEuros}
            onChange={setField("monthlyFeeEuros")}
            className={inputCls}
            style={{ width: "100%" }}
          />
        </label>
        <label className="text-sm block">
          <span className="block text-text-2 text-xs mb-1">Seats</span>
          <input type="number" step="1" min="0" value={form.seats} onChange={setField("seats")} className={inputCls} style={{ width: "100%" }} />
        </label>
        <label className="text-sm block">
          <span className="block text-text-2 text-xs mb-1">Active from</span>
          <input type="date" value={form.activeFrom} onChange={setField("activeFrom")} className={inputCls} style={{ width: "100%" }} />
        </label>
      </div>
      <button
        type="submit"
        disabled={busy}
        className="px-4 py-1.5 text-sm rounded-ctrl bg-primary text-white hover:bg-primary-hover transition-colors disabled:opacity-40"
      >
        {busy ? "Saving…" : "Save plan"}
      </button>
      {saved && <span className="ml-2 text-xs text-success">Saved</span>}
      {error && <p className="text-xs text-danger mt-2">{error}</p>}
    </form>
  );
}
